import React, { useCallback, useEffect, useRef, useState } from 'react';
import { resolveZoneByLocation } from '../../services/contact.service';
import { isValidId } from '@horseed/shared/ids';

const CITY_STORAGE_KEY = 'hz_city';
const FALLBACK_CITY = 'Hargeisa';
const GEO_STORAGE_KEY = 'hz_geo';
const LATITUDE_STORAGE_KEY = 'hz_latitude';
const LONGITUDE_STORAGE_KEY = 'hz_longitude';
const ZONE_STORAGE_KEY = 'hz_zone_id';
const ZONE_DETAIL_KEY = 'hz_zone';

// ── Geocoding helpers ────────────────────────────────────────────────────────

// Primary: BigDataCloud (no API key needed, fast)
async function reverseGeocodeBigDataCloud(latitude, longitude) {
    const params = new URLSearchParams({
        latitude: String(latitude),
        longitude: String(longitude),
        localityLanguage: 'en',
    });
    const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?${params}`);
    if (!response.ok) throw new Error('BigDataCloud request failed.');
    const data = await response.json();
    return (
        data?.city ||
        data?.locality ||
        data?.principalSubdivision ||
        data?.countryName ||
        ''
    );
}

// Fallback: Nominatim / OpenStreetMap (broader coverage)
async function reverseGeocodeNominatim(latitude, longitude) {
    const params = new URLSearchParams({
        lat: String(latitude),
        lon: String(longitude),
        format: 'json',
        'accept-language': 'en',
    });
    const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?${params}`,
        { headers: { 'User-Agent': 'HornPetroleum/1.0' } },
    );
    if (!response.ok) throw new Error('Nominatim request failed.');
    const data = await response.json();
    return (
        data?.address?.city ||
        data?.address?.town ||
        data?.address?.village ||
        data?.address?.county ||
        data?.address?.state ||
        data?.address?.country ||
        ''
    );
}

// Modal shown when location permission is denied
function LocationPermissionModal({ onRetry, onDismiss }) {
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                background: 'rgba(0,0,0,0.72)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="loc-modal-title"
          
        >
            <div
                style={{
                    position: 'relative',
                    background: '#fff',
                    borderRadius: 12,
                    padding: '2rem',
                    maxWidth: 420,
                    width: '90%',
                    textAlign: 'center',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
                }}
            >
                <div style={{ fontSize: 48, marginBottom: 12 }}>📍</div>
                <button
                    type="button"
                    onClick={onDismiss}
                    aria-label="Close location permission dialog"
                    style={{
                        position: 'absolute',
                        top: 10,
                        right: 12,
                        width: 32,
                        height: 32,
                        border: 'none',
                        borderRadius: '50%',
                        background: '#f1f4f5',
                        color: '#052730',
                        cursor: 'pointer',
                        fontSize: 22,
                        lineHeight: '32px',
                        fontWeight: 700,
                    }}
                >
                    <span aria-hidden="true">&times;</span>
                </button>
                <h4 id="loc-modal-title" style={{ marginBottom: 10, color: '#052730' }}>
                    Location Access Required
                </h4>
                <p style={{ color: '#555', marginBottom: 14, lineHeight: 1.6, fontSize: 14 }}>
                    This app needs your location to show the nearest branch and delivery zone.
                    Please allow location access in your browser.
                </p>
                <div
                    style={{
                        background: '#f5f7f8',
                        borderRadius: 8,
                        padding: '0.75rem 1rem',
                        marginBottom: 20,
                        textAlign: 'left',
                        fontSize: 13,
                        color: '#444',
                        lineHeight: 1.7,
                    }}
                >
                    <strong>How to enable:</strong>
                    <ol style={{ margin: '6px 0 0 18px', padding: 0 }}>
                        <li>Click the <strong>lock / info</strong> icon in your browser's address bar</li>
                        <li>Set <strong>Location</strong> to <em>Allow</em></li>
                        <li>Click <strong>Try Again</strong> below</li>
                    </ol>
                </div>
                <button
                    type="button"
                    onClick={onRetry}
                    style={{
                        background: 'linear-gradient(135deg, #052730, #0a5060)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 24,
                        padding: '0.6rem 2rem',
                        fontSize: 15,
                        cursor: 'pointer',
                        fontWeight: 600,
                    }}
                >
                    Try Again
                </button>
            </div>
        </div>
    );
}

// ── Storage helpers ──────────────────────────────────────────────────────────

function loadCachedLocation() {
    try {
        const city = localStorage.getItem(CITY_STORAGE_KEY) || '';
        const rawGeo = localStorage.getItem(GEO_STORAGE_KEY);
        if (rawGeo) {
            const parsed = JSON.parse(rawGeo);
            const latitude = Number(parsed?.latitude);
            const longitude = Number(parsed?.longitude);
            if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
                return { city, latitude, longitude };
            }
        }

        const rawLatitude = localStorage.getItem(LATITUDE_STORAGE_KEY);
        const rawLongitude = localStorage.getItem(LONGITUDE_STORAGE_KEY);
        if (!rawLatitude || !rawLongitude) return { city, latitude: null, longitude: null };

        const latitude = Number(rawLatitude);
        const longitude = Number(rawLongitude);
        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
            return { city, latitude, longitude };
        }
    } catch {
        /* ignore */
    }
    return { city: '', latitude: null, longitude: null };
}

function persistLocation({ city, latitude, longitude }) {
    try {
        if (city) localStorage.setItem(CITY_STORAGE_KEY, city);
        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
            const geo = { latitude, longitude, at: Date.now() };
            localStorage.setItem(GEO_STORAGE_KEY, JSON.stringify(geo));
            localStorage.setItem(LATITUDE_STORAGE_KEY, String(latitude));
            localStorage.setItem(LONGITUDE_STORAGE_KEY, String(longitude));
        }
    } catch {
        /* ignore */
    }
}

function persistZone(zone) {
    try {
        if (!zone?.id) return;
        localStorage.setItem(ZONE_STORAGE_KEY, String(zone.id));
        localStorage.setItem(ZONE_DETAIL_KEY, JSON.stringify(zone));
    } catch {
        /* ignore */
    }
}

function hasStoredZoneId() {
    try {
        // Zone ids are GUIDs — the old numeric check rejected every one of them.
        return isValidId(localStorage.getItem(ZONE_STORAGE_KEY));
    } catch {
        return false;
    }
}

// ── Geocoding (BigDataCloud → Nominatim fallback) ────────────────────────────

async function reverseGeocodeCity(latitude, longitude) {
    try {
        const city = await reverseGeocodeBigDataCloud(latitude, longitude);
        if (city) return city;
    } catch {
        /* fall through to Nominatim */
    }
    return reverseGeocodeNominatim(latitude, longitude);
}

// ── Component ────────────────────────────────────────────────────────────────

export default function CurrentLocation() {
    const [location, setLocation] = useState(() => loadCachedLocation());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [permissionDenied, setPermissionDenied] = useState(false);
    const didAutoDetect = useRef(false);

    const resolveAndPersistZone = useCallback(async (latitude, longitude) => {
        const resolved = await resolveZoneByLocation(latitude, longitude);
        if (resolved?.zone) {
            persistZone(resolved.zone);
            window.dispatchEvent(new CustomEvent('zone:changed', {
                detail: {
                    matched: resolved.matched === true,
                    zone: resolved.zone,
                },
            }));
            return true;
        }
        return false;
    }, []);

    const detectLocation = useCallback(() => {
        if (!(typeof navigator !== 'undefined' && navigator.geolocation)) {
            setError('Geolocation is not available in this browser.');
            return;
        }

        setError('');
        setPermissionDenied(false);
        setLoading(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const { latitude, longitude } = pos.coords || {};
                    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                        throw new Error('Browser did not return valid coordinates.');
                    }

                    setPermissionDenied(false);
                    setLocation((prev) => {
                        const next = { ...prev, latitude, longitude };
                        persistLocation(next);
                        return next;
                    });

                    let nextError = '';
                    try {
                        const rawCity = await reverseGeocodeCity(latitude, longitude);
                        const city = rawCity || FALLBACK_CITY;
                        const nextLocation = { city, latitude, longitude };
                        persistLocation(nextLocation);
                        setLocation(nextLocation);
                    } catch (err) {
                        setLocation((prev) => ({ ...prev, city: prev.city || FALLBACK_CITY }));
                        nextError = err?.message || 'Failed to detect your city.';
                    }

                    try {
                        const resolved = await resolveAndPersistZone(latitude, longitude);
                        if (!resolved && !nextError) {
                            nextError = 'Zone was not found for your current location.';
                        }
                    } catch (err) {
                        if (!nextError) {
                            nextError = err?.message || 'Failed to resolve your zone.';
                        }
                    }

                    setError(nextError);
                } catch (err) {
                    setError(err?.message || 'Failed to detect your city.');
                } finally {
                    setLoading(false);
                }
            },
            (err) => {
                // code 1 = PERMISSION_DENIED
                if (err?.code === 1) {
                    setPermissionDenied(true);
                }
                setLocation((prev) => ({ ...prev, city: prev.city || FALLBACK_CITY }));
                setError(err?.message || 'Location permission denied.');
                setLoading(false);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
        );
    }, [resolveAndPersistZone]);

    // Proactively check permission state and watch for changes
    useEffect(() => {
        if (!navigator?.permissions?.query) return;
        let permStatus;
        navigator.permissions.query({ name: 'geolocation' }).then((status) => {
            permStatus = status;
            if (status.state === 'denied') {
                setPermissionDenied(true);
            }
            status.onchange = () => {
                if (status.state === 'granted') {
                    setPermissionDenied(false);
                    didAutoDetect.current = false;
                    detectLocation();
                } else if (status.state === 'denied') {
                    setPermissionDenied(true);
                }
            };
        }).catch(() => { /* Permissions API not supported */ });
        return () => {
            if (permStatus) permStatus.onchange = null;
        };
    }, [detectLocation]);

    useEffect(() => {
        if (didAutoDetect.current) return;
        if (
            location.city &&
            Number.isFinite(location.latitude) &&
            Number.isFinite(location.longitude)
        ) {
            if (hasStoredZoneId()) return;
            didAutoDetect.current = true;
            resolveAndPersistZone(location.latitude, location.longitude)
                .catch((err) => {
                    setError(err?.message || 'Failed to resolve your zone.');
                });
            return;
        }
        didAutoDetect.current = true;
        detectLocation();
    }, [
        location.city,
        location.latitude,
        location.longitude,
        detectLocation,
        resolveAndPersistZone,
    ]);

    const coordinates = Number.isFinite(location.latitude) && Number.isFinite(location.longitude)
        ? `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`
        : '';
    const label = loading
        ? 'Detecting...'
        : permissionDenied
            ? 'Enable Location'
            : location.city || FALLBACK_CITY;
    const icon = loading
        ? 'fa-spinner fa-pulse'
        : permissionDenied
            ? 'fa-exclamation-triangle'
            : 'fa-map-marker-alt';
    const title = permissionDenied
        ? 'Location access denied — click to see how to enable it'
        : error
            ? error
            : coordinates
                ? `Current location: ${coordinates}. Click to refresh.`
                : 'Click to detect your current city';

    return (
        <>
            {permissionDenied && (
                <LocationPermissionModal
                    onDismiss={() => setPermissionDenied(false)}
                    onRetry={() => {
                        setPermissionDenied(false);
                        detectLocation();
                    }}
                />
            )}
            <button
                type="button"
                className="btn btn-sm btn-link text-white p-0 d-inline-flex align-items-center"
                onClick={() => {
                    if (!loading) {
                        if (permissionDenied) setPermissionDenied(true); // re-show modal
                        else detectLocation();
                    }
                }}
                disabled={loading}
                aria-live="polite"
                title={title}
            >
                <i
                    aria-hidden="true"
                    className={`fas ${icon} fa-lg`}
                />
                <span className="ms-2 small">{label}</span>
            </button>
        </>
    );
}
